import React, { useEffect, useMemo } from "react";
import "./SelectScreen.scss";
import Logo from "../../../assets/images/LogoNew.svg";
import { useNavigate } from "react-router-dom";
import {
  getStoredAuthMessage,
  getStoredAuthRedirectPath,
} from "../../../util/authResume";
import {
  FiArrowRight,
  FiBriefcase,
  FiTrendingUp,
  FiUser,
} from "react-icons/fi";

const normalizeMessageToken = (value = "") => `${value || ""}`.trim().toLowerCase();

const isUnauthorizedResumeMessage = (value = {}) => {
  const code = normalizeMessageToken(value?.code);
  const message = normalizeMessageToken(value?.message || value?.error);

  return (
    code === "unauthorized" ||
    code === "unauthorized_access" ||
    message === "unauthorized access"
  );
};

const resolveAuthMessage = (authMessage) => {
  if (!authMessage) {
    return "";
  }

  if (typeof authMessage !== "string") {
    return isUnauthorizedResumeMessage(authMessage) ? "" : authMessage;
  }

  const trimmed = authMessage.trim();

  if (!trimmed) {
    return "";
  }

  if (normalizeMessageToken(trimmed) === "unauthorized access") {
    return "";
  }

  if (trimmed.startsWith("{") || trimmed.startsWith("[")) {
    try {
      const parsed = JSON.parse(trimmed);

      if (isUnauthorizedResumeMessage(parsed)) {
        return "";
      }

      return parsed?.message || parsed?.error || "Your session expired. Please log in again.";
    } catch (e) {
      // Not JSON, keep the original message below.
    }
  }

  return authMessage;
};

const SelectScreen = () => {
  const navigate = useNavigate();
  const queryParams = window.location.search || "";

  useEffect(() => {
    const redirectPath = getStoredAuthRedirectPath();
    if (redirectPath) {
      navigate(redirectPath, { replace: true });
    }
  }, [navigate]);

  const authMessage = resolveAuthMessage(getStoredAuthMessage());

  const accountCards = useMemo(
    () => [
      {
        eyebrow: "Personal banking",
        title: "Individual Plus",
        summary:
          "For individuals who want a straightforward account opening journey with everyday digital banking access.",
        highlights: [
          "Simple onboarding for personal use",
          "Digital banking, card access, and core account services",
          "Built for lower-complexity personal banking needs",
        ],
        route: `/individual-plus${queryParams}`,
        icon: FiUser,
        tone: "individual",
      },
      {
        eyebrow: "Business banking",
        title: "Business Plus",
        summary:
          "For operating businesses that need core banking capabilities without the enhanced financial-institution review path.",
        highlights: [
          "Designed for non-financial-service businesses",
          "Covers operating, holding, consulting, technology, and similar business models",
          "Best fit when your business does not require Prime-only follow-up pages",
        ],
        route: `/business-plus${queryParams}`,
        icon: FiBriefcase,
        tone: "business-plus",
      },
      {
        eyebrow: "Business banking",
        title: "Business Prime",
        summary:
          "For licensed financial-service and digital-asset businesses that need the Prime due-diligence flow.",
        highlights: [
          "For MSBs, VASPs, brokerages, funds, and related financial-service models",
          "Includes the deeper business, compliance, and digital-asset review path",
          "Use this when your business requires the Prime application journey",
        ],
        route: `/business-prime${queryParams}`,
        icon: FiTrendingUp,
        tone: "business-prime",
      },
    ],
    [queryParams]
  );

  return (
    <div className="select-screen">
      <div className="select-screen__orb select-screen__orb--left" />
      <div className="select-screen__orb select-screen__orb--right" />

      <div className="select-screen__shell">
        <header className="select-screen__topbar">
          <img src={Logo} alt="FV Bank" className="select-screen__logo" />
        </header>

        <main className="select-screen__content">
          <section className="select-screen__section">
            <div className="select-screen__section-heading">
              <div>
                <span className="select-screen__section-kicker">Start a new application</span>
                <h2>Select the product that matches your intended journey.</h2>
              </div>
            </div>

            {authMessage ? (
              <div className="select-screen__message" role="status">
                {authMessage}
              </div>
            ) : null}

            <div className="select-screen__card-grid">
              {accountCards.map(({ eyebrow, title, summary, highlights, route, icon: Icon, tone }) => (
                <article key={title} className={`select-screen__card select-screen__card--${tone}`}>
                  <div className="select-screen__card-head">
                    <span className="select-screen__card-eyebrow">{eyebrow}</span>
                    <div className="select-screen__card-icon">
                      <Icon size={20} />
                    </div>
                  </div>

                  <h3>{title}</h3>
                  <p className="select-screen__card-summary">{summary}</p>

                  <ul className="select-screen__feature-list">
                    {highlights.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>

                  <button
                    className="select-screen__primary-action"
                    onClick={() => navigate(route)}
                    type="button"
                  >
                    Continue to {title}
                    <FiArrowRight size={18} />
                  </button>
                </article>
              ))}
            </div>
          </section>
        </main>
      </div>
    </div>
  );
};

export default SelectScreen;
